import React from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { useState } from 'react';
import MyDataTable from '../../../components/MyDataTable';
import { translate } from '../../../services/translate';
import { Pagination } from '../../../components/Pagination';
import Filters from './Filters';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { DispatchParamList } from '../../../typings/Params';
import { dispatchService } from '../../../services/central-api/dispatch';
import EditModal from './EditModal';
import DeleteModal from '../../../components/DeleteModal';
import Toast from 'react-native-toast-message';
import { ClientError } from '../../../services/central-api/base-service';
import useFilters from '../../../components/Filter/hooks/useFilters';
import CustomButton from '../../../components/CustomButton';
import FilterInput from '../../../components/Filter/components/FilterInput';
import { useAuth } from '../../../context/Auth';
import DropdownButton from '../../../components/DropdownButton';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { useQuery } from '@tanstack/react-query';
import { RootNavigation } from '../../../Routes';

interface Props {
    navigation: RootNavigation;
}

export default function FinishCategories({ navigation }: Props) {
    const route = useRoute<RouteProp<DispatchParamList, 'FinishCategories'>>();
    const { user: authenticatedUser } = useAuth();

    const [filters, setFilters] = useFilters<DispatchParamList['FinishCategories']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });

    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');

    const [selectedFinishCategory, setSelectedFinishCategory] = useState<number>();
    const [isModalVisible, setModalVisible] = useState<boolean>(false);
    const [selectedFinishCategoryToDelete, setSelectedFinishCategoryToDelete] = useState<FinishCategory | null>(null);
    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['getPaginatedFinishCategories', filters],
        queryFn: () => dispatchService.getPaginatedFinishCategories(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    async function confirmDelete(finishCategory: FinishCategory) {
        try {
            await dispatchService.deleteFinishCategory(finishCategory.id);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            setDeleteModalVisible(false);
            refetch();
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    function handleFormClose() {
        setSelectedFinishCategory(undefined);
        setModalVisible(false);
        refetch();
    }

    return (
        <Container>
            <Header
                title={translate('finishCategories')}
                actions={
                    <CustomButton
                        text={translate('register')}
                        onPress={() => {
                            setSelectedFinishCategory(undefined);
                            setModalVisible(true);
                        }}
                    />
                }
            />
            <Card>
                <DataTableHeader
                    zIndex={2}
                    title={translate('registeredFinishCategories')}
                    filter={<Filters
                        filters={filters}
                        setFilters={setFilters}
                        textFilter={textFilter}
                        setTextFilter={setTextFilter}
                    />}
                    filterInput={<FilterInput
                        value={textFilter}
                        placeholder={translate('typeToSearch')}
                        onChange={setTextFilter}
                        onKeyPress={handleKeyDown}
                    />}
                />

                <DataTableContainer
                    dataTable={<MyDataTable
                        progressPending={isLoading}
                        columns={[
                            {
                                name: 'ID',
                                selector: row => row.id,
                                grow: 0
                            },
                            {
                                name: translate('name'),
                                selector: row => row.name,
                                wrap: true,
                            },
                            {
                                name: translate('actingBody'),
                                selector: row => row.actingBody.name,
                                wrap: true,
                            },
                            {
                                cell: row => (
                                    <DropdownButton
                                        actions={[
                                            {
                                                content: translate('editInformation'),
                                                onPress: () => {
                                                    setSelectedFinishCategory(row.id);
                                                    setModalVisible(true);
                                                }
                                            },
                                            {
                                                content: translate('delete'),
                                                variant: 'danger',
                                                onPress: () => {
                                                    setSelectedFinishCategoryToDelete(row);
                                                    setDeleteModalVisible(true);
                                                },
                                            }
                                        ]}
                                    />
                                ),
                                wrap: true,
                                button: true
                            }
                        ]}
                        data={data?.rows ?? []}
                    />}
                    pagination={<Pagination
                        totalItems={data?.count ?? 0}
                        currentPage={filters.page}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({
                                ...old,
                                limit: pageSize,
                                page: 0,
                            }));
                            navigation.setParams({
                                ...route.params,
                                limit: pageSize,
                                page: 1,
                            });
                        }}
                        setPage={page => {
                            setFilters(old => ({
                                ...old,
                                page
                            }));
                            navigation.setParams({
                                ...route.params,
                                page: page + 1
                            });
                        }}
                    />}
                />
            </Card>

            <EditModal
                onClose={handleFormClose}
                finishCategoryId={selectedFinishCategory ?? undefined}
                isModalVisible={isModalVisible}
                setModalVisible={setModalVisible}
                authenticatedUser={authenticatedUser}
            />

            {selectedFinishCategoryToDelete &&
                <DeleteModal
                    setModalVisible={setDeleteModalVisible}
                    isModalVisible={isDeleteModalVisible}
                    itemName={selectedFinishCategoryToDelete.name}
                    onSubmit={async () => {
                        await confirmDelete(selectedFinishCategoryToDelete);
                    }}
                />
            }
        </Container>
    );
}
